import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug } from '@/mocks/articleContent';

const SITE_URL = import.meta.env.VITE_SITE_URL;

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
          <div className="text-center px-4">
            <span className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
              <i className="ri-article-line text-2xl"></i>
            </span>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">Article Not Found</h1>
            <p className="text-sm text-foreground-500 mb-5">The article you are looking for does not exist or has been moved.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line"></i> Back to Home
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <article className="bg-background-50 pt-24 pb-16">
        {/* Breadcrumb */}
        <div className="max-w-3xl mx-auto px-4 md:px-6 mb-8">
          <nav className="flex items-center gap-2 text-xs text-foreground-400" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">Home</Link>
            <span>/</span>
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">Articles</Link>
            <span>/</span>
            <span className="text-foreground-700 font-medium truncate max-w-[200px]">{article.title}</span>
          </nav>
        </div>

        {/* Hero Image */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 mb-10">
          <div className="aspect-[21/9] w-full rounded-2xl overflow-hidden">
            <img
              src={article.heroImageUrl}
              alt={article.heroImageAlt}
              title={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <header className="mb-10">
            <div className="flex items-center gap-3 text-xs text-foreground-400 mb-4">
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.readTime}</span>
              <span>·</span>
              <span className="px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full font-medium whitespace-nowrap">{article.tags[0]}</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base text-foreground-500 leading-relaxed">
              {article.excerpt}
            </p>
          </header>

          {/* Article Body */}
          <div
            className="prose-custom space-y-8"
            dangerouslySetInnerHTML={{
              __html: article.bodyHtml
                .replace(/<section>/g, '<section class="space-y-4">')
                .replace(/<h2/g, '<h2 class="font-heading text-2xl font-bold text-foreground-900 mb-4"')
                .replace(/<h3/g, '<h3 class="font-heading text-lg font-semibold text-foreground-900 mt-6 mb-3"')
                .replace(/<p>/g, '<p class="text-sm text-foreground-600 leading-relaxed mb-4">')
                .replace(/<li>/g, '<li class="flex items-start gap-3 text-sm text-foreground-600 mb-2">'),
            }}
          />

          {/* Conclusion CTA */}
          <section className="mt-12 bg-accent-50 rounded-xl p-6 border border-accent-200/50">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-3">
              Start Exploring the Best VigorBuy Spreadsheet Today
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-5">
              Now that you have read this guide, you are ready to start discovering amazing products through the <strong>VigorBuy Spreadsheet</strong>. Browse categories, compare sellers, and shop with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
              >
                Browse Spreadsheet Now <i className="ri-arrow-right-line"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer border border-background-200"
              >
                <i className="ri-home-line"></i> Back to Home
              </Link>
            </div>
          </section>

          {/* Related Articles */}
          <section className="mt-12 pt-10 border-t border-background-200/70">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-5">Related Articles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { slug: 'how-to-use-vigorbuy-spreadsheet-beginner', title: 'How to Use VigorBuy Spreadsheet: Beginner Guide' },
                { slug: 'vigorbuy-spreadsheet-safety-tips', title: 'VigorBuy Spreadsheet Safety: 10 Essential Tips' },
                { slug: 'how-to-find-trusted-vigorbuy-sellers', title: 'How to Find Trusted VigorBuy Sellers' },
                { slug: 'best-vigorbuy-spreadsheet-shoes-2026', title: 'Best VigorBuy Spreadsheet Shoes 2026' },
              ].filter((r) => r.slug !== slug).slice(0, 3).map((related) => (
                <Link
                  key={related.slug}
                  to={`/article/${related.slug}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                  className="flex items-center gap-3 p-4 bg-background-100 rounded-lg border border-background-200/70 hover:border-accent-200 transition-colors cursor-pointer group"
                >
                  <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 group-hover:bg-accent-200 transition-colors">
                    <i className="ri-article-line text-sm"></i>
                  </span>
                  <span className="text-sm text-foreground-700 group-hover:text-accent-600 transition-colors line-clamp-2 leading-snug">{related.title}</span>
                </Link>
              ))}
            </div>
          </section>
        </div>

        {/* Article Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              image: article.heroImageUrl,
              datePublished: article.date.replace('June ', '2026-06-').replace('July ', '2026-07-').replace(/(\d+), 2026/, (m: string, d: string) => d.padStart(2, '0')),
              author: { '@type': 'Organization', name: 'VigorBuy Spreadsheet Hub' },
              publisher: { '@type': 'Organization', name: 'VigorBuy Spreadsheet Hub' },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
                { '@type': 'ListItem', position: 2, name: article.title, item: `${SITE_URL}/article/${article.slug}` },
              ],
            }),
          }}
        />
      </article>
      <Footer />
    </>
  );
}